import React from 'react'
import PropTypes from 'prop-types'
import { Tooltip } from '@material-ui/core'

export const ALL_AVAILABLE_COLUMNS = [
  { name: 'transdate', displayName: 'Date', accessorKey: 'transdate', group: 'base', dataType: 'date', align: 'left', sortable: true, filterable: false, hidden: false, exclude: false, width: 120, formatString: 'date' },
  { name: 'accountNumber', displayName: 'Acct Number', accessorKey: 'accountNumber', group: 'client', dataType: 'string', align: 'left', sortable: true, filterable: false, hidden: false, exclude: false, width: 130, formatString: 'title' },
  { name: 'accountName', displayName: 'Account', accessorKey: 'accountName', group: 'client', dataType: 'string', align: 'left', sortable: true, filterable: false, hidden: false, exclude: false, width: 180, formatString: 'title' },
  { name: 'accountLongName', displayName: 'Account Long Name', accessorKey: 'accountLongName', group: 'accountLongName', dataType: 'string', align: 'left', sortable: true, filterable: false, hidden: true, exclude: true, width: 220, formatString: 'title' },
  { name: 'productname', displayName: 'Product', accessorKey: 'productname', group: 'product', dataType: 'string', align: 'left', sortable: true, filterable: false, hidden: false, exclude: false, width: 200, formatString: 'title' },
  { name: 'ticker', displayName: 'Ticker', accessorKey: 'ticker', group: 'product', dataType: 'string', align: 'left', sortable: true, filterable: false, hidden: false, exclude: false, width: 120, formatString: 'title' },
  { name: 'transamount', displayName: 'Amount', accessorKey: 'transamount', group: 'base', dataType: 'decimal', align: 'right', sortable: true, filterable: false, hidden: false, exclude: false, width: 130, formatString: 'marketValue' },
  { name: 'notes', displayName: 'Notes', accessorKey: 'notes', group: 'base', dataType: 'string', align: 'left', sortable: false, filterable: false, hidden: false, exclude: false, width: 250, truncate: true },
  { name: 'transactionTypeName', displayName: 'Transaction Type', accessorKey: 'transactionTypeName', group: 'transactionType', dataType: 'string', align: 'left', sortable: true, filterable: false, hidden: false, exclude: false, width: 160, formatString: 'title' },
  { name: 'iscustodialcash', displayName: 'Custodial Cash', accessorKey: 'iscustodialcash', group: 'product', dataType: 'integer', align: 'center', sortable: true, filterable: false, hidden: true, exclude: false, width: 120, formatString: 'bool' },
  { name: 'nounits', displayName: 'Units', accessorKey: 'nounits', group: 'base', dataType: 'decimal', align: 'right', sortable: true, filterable: false, hidden: true, exclude: false, width: 100, formatString: 'number' },
  { name: 'settledate', displayName: 'Settle Date', accessorKey: 'settledate', group: 'base', dataType: 'date', align: 'left', sortable: true, filterable: false, hidden: true, exclude: false, width: 120, formatString: 'date' },
  { name: 'type', displayName: 'Type Code', accessorKey: 'type', group: 'transactionType', dataType: 'string', align: 'left', sortable: true, filterable: false, hidden: true, exclude: false, width: 100, formatString: 'title' },
  { name: 'financialtype', displayName: 'Financial Type', accessorKey: 'financialTypeName', group: 'financialType', dataType: 'string', align: 'left', sortable: true, filterable: false, hidden: true, exclude: false, width: 150, formatString: 'title' },
  { name: 'taxType', displayName: 'Tax Type', accessorKey: 'taxType', group: 'taxType', dataType: 'string', align: 'left', sortable: true, filterable: false, hidden: true, exclude: false, width: 160, formatString: 'title' },
  { name: 'advisorNotes', displayName: 'Advisor Notes', accessorKey: 'advisorNotes', group: 'advisorNotes', dataType: 'string', align: 'left', sortable: false, filterable: false, hidden: true, exclude: false, width: 250, truncate: true }
]

export const GROUP_TO_INCLUDE = {
  product: 'productDetails',
  financialType: 'financialType',
  transactionType: 'transactionTypeDetails',
  client: 'client',
  accountLongName: 'accountLongName',
  taxType: 'taxType',
  advisorNotes: 'advisorNotes'
}

export const TEXT_SEARCHABLE_COLUMNS = new Set([
  'ticker',
  'productname',
  'notes',
  'accountName',
  'accountNumber',
  'accountLongName',
  'cusip',
  'transactionTypeName'
])

export const DEFAULT_TEXT_SEARCH_FIELDS = ['ticker', 'productname', 'notes']

export function deriveTextSearchFields (columns = []) {
  const fields = new Set()
  for (const col of columns) {
    if (col?.filterable && !col?.exclude && TEXT_SEARCHABLE_COLUMNS.has(col.name)) {
      fields.add(col.name)
    }
  }
  if (fields.size === 0) {
    return [...DEFAULT_TEXT_SEARCH_FIELDS]
  }
  return [...fields]
}

export function buildDefaultColumns (includes = {}) {
  return ALL_AVAILABLE_COLUMNS
    .filter(col => {
      const includeKey = GROUP_TO_INCLUDE[col.group]
      if (!includeKey) return true
      return includes[includeKey] !== false
    })
    .map(col => ({ ...col }))
}

export function deriveIncludes (columns) {
  const includes = {}
  const groups = new Set(columns.filter(c => !c.exclude).map(c => c.group))
  for (const [group, includeKey] of Object.entries(GROUP_TO_INCLUDE)) {
    includes[includeKey] = groups.has(group)
  }
  return includes
}

function TruncatedCell ({ value, width }) {
  if (value == null || value === '') return null
  const text = String(value)
  const style = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: width ? `${width - 16}px` : '200px'
  }
  return (
    <Tooltip title={text} enterDelay={300} placement='bottom-start'>
      <div style={style}>{text}</div>
    </Tooltip>
  )
}

TruncatedCell.propTypes = {
  value: PropTypes.any,
  width: PropTypes.number
}

export function buildTanstackColumns (columns, formatter) {
  return columns
    .filter(c => !c.hidden && !c.exclude)
    .map(col => {
      const formatString = col.formatString || col.formatType || null
      const def = {
        id: col.name,
        accessorKey: col.accessorKey ?? col.name,
        header: col.displayName,
        align: col.align || 'left',
        enableSorting: col.sortable !== false,
        size: col.width || 150
      }
      if (col.truncate) {
        const colWidth = col.width || 150
        def.cell = ({ getValue }) => <TruncatedCell value={formatString ? formatter(getValue(), formatString) : getValue()} width={colWidth} />
      } else if (formatString) {
        def.cell = ({ getValue }) => formatter(getValue(), formatString)
      }
      return def
    })
}
